import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../../services/apiService'
import GenderIcon from '../../components/common/GenderIcon'

const CertificateGeneration = () => {
  const navigate = useNavigate()

  // Tab state
  const [activeTab, setActiveTab] = useState('publish')

  // Shared state
  const [sessions, setSessions] = useState([])
  const [loadingSessions, setLoadingSessions] = useState(false)

  // Publish tab state
  const [publishSession, setPublishSession] = useState('')
  const [publishGender, setPublishGender] = useState('M')
  const [publishEvents, setPublishEvents] = useState([])
  const [loadingPublishEvents, setLoadingPublishEvents] = useState(false)
  const [publishingId, setPublishingId] = useState(null)

  // Download tab state
  const [downloadSession, setDownloadSession] = useState('')
  const [downloadEvents, setDownloadEvents] = useState([])
  const [selectedDownloadEvent, setSelectedDownloadEvent] = useState('')
  const [participatingTeams, setParticipatingTeams] = useState([])
  const [selectedTeam, setSelectedTeam] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('')
  const [eventCategories, setEventCategories] = useState([])
  const [selectedGender, setSelectedGender] = useState('')
  const [loadingDownloadEvents, setLoadingDownloadEvents] = useState(false)
  const [loadingTeams, setLoadingTeams] = useState(false)
  const [downloading, setDownloading] = useState(false)

  // Load sessions on mount
  useEffect(() => {
    loadSessions()
  }, [])

  const loadSessions = async () => {
    setLoadingSessions(true)
    try {
      const response = await api.get('/client-admin/certificates/sessions')
      if (response.data.success) {
        const data = response.data.data || []
        setSessions(data)
        if (data.length > 0) {
          setPublishSession(data[0])
          setDownloadSession(data[0])
        }
      }
    } catch (error) {
      console.error('Error loading sessions:', error)
    } finally {
      setLoadingSessions(false)
    }
  }

  // Load events when publish session changes
  useEffect(() => {
    if (publishSession) loadPublishEvents()
  }, [publishSession])

  // Load events when download session changes
  useEffect(() => {
    if (downloadSession) loadDownloadEvents()
  }, [downloadSession])

  const loadPublishEvents = async () => {
    setLoadingPublishEvents(true)
    try {
      const response = await api.get(`/client-admin/certificates/events-by-session?session=${publishSession}`)
      if (response.data.success) {
        setPublishEvents(response.data.data || [])
      }
    } catch (error) {
      console.error('Error loading publish events:', error)
    } finally {
      setLoadingPublishEvents(false)
    }
  }

  const loadDownloadEvents = async () => {
    setLoadingDownloadEvents(true)
    setSelectedDownloadEvent('')
    setParticipatingTeams([])
    setEventCategories([])
    setSelectedTeam('')
    setSelectedCategory('')
    setSelectedGender('')
    try {
      const response = await api.get(`/client-admin/certificates/events-by-session?session=${downloadSession}`)
      if (response.data.success) {
        // Only show published events in download tab
        const published = (response.data.data || []).filter(e => e.publication_status === 'published')
        setDownloadEvents(published)
      }
    } catch (error) {
      console.error('Error loading download events:', error)
    } finally {
      setLoadingDownloadEvents(false)
    }
  }

  const handlePublish = async (eventId, gender, action) => {
    setPublishingId(`${eventId}-${gender}`)
    try {
      const response = await api.post('/client-admin/certificates/publish', { event_id: eventId, gender, action })
      if (response.data.success) {
        showNotification(response.data.message, 'success')
        loadPublishEvents()
      }
    } catch (error) {
      showNotification(error.response?.data?.message || 'Failed to update publication status', 'error')
    } finally {
      setPublishingId(null)
    }
  }

  // Download tab: when event is selected, load participating teams
  const handleDownloadEventChange = async (eventId) => {
    setSelectedDownloadEvent(eventId)
    setParticipatingTeams([])
    setEventCategories([])
    setSelectedTeam('')
    setSelectedCategory('')
    setSelectedGender('')

    if (!eventId) return

    setLoadingTeams(true)
    try {
      const teamsRes = await api.get(`/client-admin/certificates/participating-states?event_id=${eventId}`)
      if (teamsRes.data.success) {
        setParticipatingTeams(teamsRes.data.data || [])
        setEventCategories(teamsRes.data.categories || [])
      }
    } catch (error) {
      console.error('Error loading teams:', error)
    } finally {
      setLoadingTeams(false)
    }
  }

  const handleDownloadCertificates = async () => {
    if (!selectedDownloadEvent || !selectedTeam || !selectedGender) {
      showNotification('Please select all options', 'error')
      return
    }
    if (eventCategories.length > 0 && !selectedCategory) {
      showNotification('Please select a category', 'error')
      return
    }

    setDownloading(true)
    try {
      const payload = {
        event_id: parseInt(selectedDownloadEvent),
        team_id: parseInt(selectedTeam),
        gender: selectedGender
      }
      if (selectedCategory) {
        payload.category = selectedCategory
      }
      const response = await api.post('/client-admin/certificates/download-all-team', payload, {
        responseType: 'blob',
        headers: { 'Accept': 'application/pdf' }
      })

      if (response.data.type === 'application/json') {
        const text = await response.data.text()
        const json = JSON.parse(text)
        showNotification(json.message || 'Error generating certificates', 'error')
        return
      }

      const blob = new Blob([response.data], { type: 'application/pdf' })
      const downloadUrl = window.URL.createObjectURL(blob)
      const team = participatingTeams.find(t => t.team_id == selectedTeam)
      const filePrefix = (team?.association_name || 'Team').replace(/[^a-zA-Z0-9]/g, '_')
      const genderLabel = selectedGender === 'M' ? 'Male' : 'Female'
      const filename = `${filePrefix}_${genderLabel}_Certificates.pdf`

      const pdfWindow = window.open(downloadUrl, '_blank')
      if (!pdfWindow) {
        const link = document.createElement('a')
        link.href = downloadUrl
        link.download = filename
        link.style.display = 'none'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }

      setTimeout(() => window.URL.revokeObjectURL(downloadUrl), 5000)
      showNotification('Certificate PDF opened in new tab', 'success')
    } catch (error) {
      console.error('Error downloading certificates:', error)
      let errorMsg = 'Error generating certificates'
      if (error.response?.data) {
        try {
          // Response might be a blob (since responseType is 'blob'), parse it as JSON
          const text = error.response.data instanceof Blob
            ? await error.response.data.text()
            : JSON.stringify(error.response.data)
          const json = JSON.parse(text)
          errorMsg = json.message || errorMsg
        } catch (e) { /* ignore parse error */ }
      }
      showNotification(errorMsg, 'error')
    } finally {
      setDownloading(false)
    }
  }

  const showNotification = (message, type) => {
    const notification = document.createElement('div')
    notification.style.cssText = `
      position: fixed; top: 20px; right: 20px; padding: 1rem 1.5rem; border-radius: 8px;
      color: white; font-weight: 600; z-index: 10000; display: flex; align-items: center; gap: 10px;
      background: ${type === 'success' ? 'linear-gradient(135deg, #10b981, #059669)' : 'linear-gradient(135deg, #ef4444, #dc2626)'};
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    `
    notification.innerHTML = `<i class="fas fa-${type === 'success' ? 'check-circle' : 'exclamation-circle'}"></i> ${message}`
    document.body.appendChild(notification)
    setTimeout(() => notification.remove(), 3000)
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' })
  }

  const tabStyle = (isActive) => ({
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    padding: '1rem 1.5rem',
    border: 'none',
    background: isActive ? '#fff5f5' : 'white',
    color: isActive ? '#c00f15' : '#6b7280',
    fontWeight: '600',
    fontSize: '1rem',
    cursor: 'pointer',
    borderBottom: isActive ? '3px solid #c00f15' : '3px solid transparent',
    transition: 'all 0.2s'
  })

  const selectStyle = {
    width: '100%',
    padding: '10px 12px',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '0.95rem',
    background: 'white'
  }

  const labelStyle = {
    display: 'block',
    marginBottom: '0.5rem',
    fontWeight: '600',
    color: '#374151',
    fontSize: '0.9rem'
  }

  // Publish tab: only tournaments that have results for the selected gender
  const visiblePublishEvents = publishEvents.filter(
    e => (e.genders || []).some(g => g.gender === publishGender)
  )

  return (
    <div style={{ padding: '1.5rem 2rem', width: '100%', minHeight: '100vh', background: '#f3f4f6' }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #c00f15 0%, #a00d12 50%, #c00f15 100%)',
        padding: '1.5rem 2rem',
        color: 'white',
        borderRadius: '12px',
        marginBottom: '1.5rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <h1 style={{ margin: '0 0 0.5rem 0', fontSize: '1.75rem', fontWeight: '700', color: '#ffffff' }}>
            <i className="fas fa-certificate" style={{ marginRight: '12px' }}></i>
            Certificate Management
          </h1>
          <p style={{ margin: 0, fontSize: '1rem', color: '#ffffff' }}>
            Publish and download certificates for tournament participants
          </p>
        </div>
        <button
          onClick={() => navigate('/admin/dashboard')}
          style={{
            background: 'white',
            border: 'none',
            color: '#c00f15',
            borderRadius: '8px',
            padding: '10px 20px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '0.85rem',
            fontWeight: '600',
            whiteSpace: 'nowrap',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
          }}
        >
          <i className="fas fa-arrow-left"></i> Back
        </button>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        background: 'white',
        borderRadius: '12px',
        overflow: 'hidden',
        marginBottom: '1.5rem',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
      }}>
        <button style={tabStyle(activeTab === 'publish')} onClick={() => setActiveTab('publish')}>
          <i className="fas fa-upload"></i> Publish Certificate
        </button>
        <button style={tabStyle(activeTab === 'download')} onClick={() => setActiveTab('download')}>
          <i className="fas fa-download"></i> Download Certificate
        </button>
      </div>

      {/* ===================== PUBLISH TAB ===================== */}
      {activeTab === 'publish' && (
        <div>
          {/* Session Selection */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '1.5rem',
            marginBottom: '1.5rem',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}>
            <div style={{ display: 'flex', gap: '2.5rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <div style={{ flex: '1 1 260px', maxWidth: '300px' }}>
                <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.1rem', color: '#1f2937', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <i className="fas fa-calendar-alt" style={{ color: '#c00f15' }}></i>
                  Select Session
                </h3>
                <select
                  value={publishSession}
                  onChange={e => setPublishSession(e.target.value)}
                  style={selectStyle}
                  disabled={loadingSessions}
                >
                  <option value="">Select Session</option>
                  {sessions.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>

              <div>
                <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.1rem', color: '#1f2937', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <i className="fas fa-venus-mars" style={{ color: '#c00f15' }}></i>
                  Select Gender
                </h3>
                <div style={{ display: 'flex', gap: '10px' }}>
                  {[{ v: 'M', label: 'Male', icon: 'male' }, { v: 'F', label: 'Female', icon: 'female' }].map(g => {
                    const active = publishGender === g.v
                    return (
                      <button
                        key={g.v}
                        onClick={() => setPublishGender(g.v)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '8px',
                          padding: '10px 22px', borderRadius: '8px', fontWeight: '600',
                          fontSize: '0.95rem', cursor: 'pointer', transition: 'all 0.2s',
                          border: active ? '2px solid #c00f15' : '2px solid #e5e7eb',
                          background: active ? '#c00f15' : 'white',
                          color: active ? '#fff' : '#6b7280'
                        }}
                      >
                        <GenderIcon gender={g.v} style={{ marginRight: '0.4rem' }} /> {g.label}
                      </button>
                    )
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Events Table */}
          {loadingPublishEvents ? (
            <div style={{ background: 'white', borderRadius: '12px', padding: '3rem', textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
              <i className="fas fa-spinner fa-spin" style={{ fontSize: '2rem', color: '#c00f15' }}></i>
              <p style={{ marginTop: '1rem', color: '#6b7280' }}>Loading tournaments...</p>
            </div>
          ) : visiblePublishEvents.length > 0 ? (
            <div style={{
              background: 'white',
              borderRadius: '12px',
              overflow: 'hidden',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
            }}>
              <div style={{
                background: 'linear-gradient(to right, #f8fafc, #f1f5f9)',
                padding: '1rem 1.5rem',
                borderBottom: '1px solid #e5e7eb'
              }}>
                <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#1f2937', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <i className="fas fa-trophy" style={{ color: '#c00f15' }}></i>
                  Tournaments ({visiblePublishEvents.length})
                </h3>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ background: 'linear-gradient(to right, #c00f15, #a00d12, #c00f15)' }}>
                  <tr>
                    <th style={{ padding: '12px 16px', color: '#fff', textAlign: 'left', fontSize: '0.8rem', fontWeight: '600', textTransform: 'uppercase', background: 'transparent' }}>#</th>
                    <th style={{ padding: '12px 16px', color: '#fff', textAlign: 'left', fontSize: '0.8rem', fontWeight: '600', textTransform: 'uppercase', background: 'transparent' }}>Tournament</th>
                    <th style={{ padding: '12px 16px', color: '#fff', textAlign: 'left', fontSize: '0.8rem', fontWeight: '600', textTransform: 'uppercase', background: 'transparent' }}>Date</th>
                    <th style={{ padding: '12px 16px', color: '#fff', textAlign: 'left', fontSize: '0.8rem', fontWeight: '600', textTransform: 'uppercase', background: 'transparent' }}>Venue</th>
                    <th style={{ padding: '12px 16px', color: '#fff', textAlign: 'center', fontSize: '0.8rem', fontWeight: '600', textTransform: 'uppercase', background: 'transparent' }}>Status</th>
                    <th style={{ padding: '12px 16px', color: '#fff', textAlign: 'center', fontSize: '0.8rem', fontWeight: '600', textTransform: 'uppercase', background: 'transparent' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {visiblePublishEvents.map((event, index) => {
                    const genderEntry = (event.genders || []).find(g => g.gender === publishGender)
                    const isPublished = genderEntry?.publication_status === 'published'
                    const key = `${event.id}-${publishGender}`
                    const isBusy = publishingId === key
                    return (
                      <tr key={key} style={{ borderBottom: '1px solid #e5e7eb', background: index % 2 === 0 ? 'white' : '#fafbfc' }}>
                        <td style={{ padding: '12px 16px', color: '#6b7280' }}>{index + 1}</td>
                        <td style={{ padding: '12px 16px', fontWeight: '600', color: '#1f2937' }}>{event.event_name}</td>
                        <td style={{ padding: '12px 16px', color: '#4b5563', fontSize: '0.9rem' }}>
                          {formatDate(event.event_start_date)} - {formatDate(event.event_end_date)}
                        </td>
                        <td style={{ padding: '12px 16px', color: '#4b5563' }}>{event.event_venue || '-'}</td>
                        <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                          <span style={{
                            padding: '4px 12px',
                            borderRadius: '20px',
                            fontSize: '0.8rem',
                            fontWeight: '600',
                            background: isPublished ? '#d1fae5' : '#fee2e2',
                            color: isPublished ? '#065f46' : '#991b1b'
                          }}>
                            {isPublished ? 'Published' : 'Not Published'}
                          </span>
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                          <button
                            onClick={() => handlePublish(event.id, publishGender, isPublished ? 'unpublish' : 'publish')}
                            disabled={isBusy}
                            style={{
                              padding: '8px 16px',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: isBusy ? 'not-allowed' : 'pointer',
                              fontWeight: '600',
                              fontSize: '0.85rem',
                              color: 'white',
                              background: isPublished
                                ? 'linear-gradient(135deg, #ef4444, #dc2626)'
                                : 'linear-gradient(135deg, #10b981, #059669)',
                              opacity: isBusy ? 0.7 : 1
                            }}
                          >
                            {isBusy ? (
                              <><i className="fas fa-spinner fa-spin"></i> ...</>
                            ) : isPublished ? (
                              <><i className="fas fa-times-circle"></i> Unpublish</>
                            ) : (
                              <><i className="fas fa-check-circle"></i> Publish</>
                            )}
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          ) : publishSession ? (
            <div style={{ background: 'white', borderRadius: '12px', padding: '3rem', textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
              <i className="fas fa-trophy" style={{ fontSize: '3rem', color: '#d1d5db', marginBottom: '1rem' }}></i>
              <h3 style={{ margin: '0 0 0.5rem 0', color: '#374151' }}>No Tournaments Found</h3>
              <p style={{ margin: 0, color: '#6b7280' }}>
                {publishEvents.length > 0
                  ? `No tournaments with ${publishGender === 'M' ? 'Male' : 'Female'} results for session ${publishSession}`
                  : `No tournaments with results found for session ${publishSession}`}
              </p>
            </div>
          ) : null}
        </div>
      )}

      {/* ===================== DOWNLOAD TAB ===================== */}
      {activeTab === 'download' && (
        <div>
          {/* Selection Card */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '1.5rem',
            marginBottom: '1.5rem',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}>
            <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.1rem', color: '#1f2937', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <i className="fas fa-filter" style={{ color: '#c00f15' }}></i>
              Select Options
            </h3>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem', alignItems: 'end' }}>
              {/* Session */}
              <div>
                <label style={labelStyle}>Session *</label>
                <select
                  value={downloadSession}
                  onChange={e => setDownloadSession(e.target.value)}
                  style={selectStyle}
                  disabled={loadingSessions}
                >
                  <option value="">Select Session</option>
                  {sessions.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>

              {/* Tournament */}
              <div>
                <label style={labelStyle}>Tournament *</label>
                <select
                  value={selectedDownloadEvent}
                  onChange={e => handleDownloadEventChange(e.target.value)}
                  style={{ ...selectStyle, opacity: !downloadSession ? 0.6 : 1 }}
                  disabled={!downloadSession || loadingDownloadEvents}
                >
                  <option value="">Select Tournament</option>
                  {downloadEvents.map(event => (
                    <option key={event.id} value={event.id}>{event.event_name}</option>
                  ))}
                </select>
                {downloadSession && downloadEvents.length === 0 && !loadingDownloadEvents && (
                  <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.75rem', color: '#ef4444' }}>
                    No published tournaments for this session
                  </p>
                )}
              </div>

              {/* State / Team */}
              <div>
                <label style={labelStyle}>State (User) *</label>
                <select
                  value={selectedTeam}
                  onChange={e => { setSelectedTeam(e.target.value); setSelectedCategory(''); setSelectedGender(''); }}
                  style={{ ...selectStyle, opacity: !selectedDownloadEvent ? 0.6 : 1 }}
                  disabled={!selectedDownloadEvent || loadingTeams}
                >
                  <option value="">Select State</option>
                  {participatingTeams.map(t => (
                    <option key={t.team_id} value={t.team_id}>
                      {t.state_name ? `${t.state_name} - ` : ''}{t.association_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Category - only show if event has categories */}
              {eventCategories.length > 0 && (
                <div>
                  <label style={labelStyle}>Category *</label>
                  <select
                    value={selectedCategory}
                    onChange={e => { setSelectedCategory(e.target.value); setSelectedGender(''); }}
                    style={{ ...selectStyle, opacity: !selectedTeam ? 0.6 : 1 }}
                    disabled={!selectedTeam}
                  >
                    <option value="">Select Category</option>
                    {eventCategories.map((cat, idx) => (
                      <option key={idx} value={cat.name}>
                        {cat.name.replace(/\s*[\(\-]?\s*(male|female)\s*\)?/gi, '').trim()}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Gender */}
              <div>
                <label style={labelStyle}>Gender *</label>
                {(() => {
                  const team = participatingTeams.find(t => String(t.team_id) === String(selectedTeam));
                  const hasMale = team ? team.has_male : true;
                  const hasFemale = team ? team.has_female : true;
                  // If category is selected, also check category's allowed genders
                  const selectedCat = eventCategories.find(c => c.name === selectedCategory);
                  const catAllowsMale = selectedCat ? selectedCat.genders.includes('M') : true;
                  const catAllowsFemale = selectedCat ? selectedCat.genders.includes('F') : true;
                  const showMale = hasMale && catAllowsMale;
                  const showFemale = hasFemale && catAllowsFemale;
                  const isDisabled = !selectedTeam || (eventCategories.length > 0 && !selectedCategory);
                  return (
                    <>
                      <select
                        value={selectedGender}
                        onChange={e => setSelectedGender(e.target.value)}
                        style={{ ...selectStyle, opacity: isDisabled ? 0.6 : 1 }}
                        disabled={isDisabled}
                      >
                        <option value="">Select Gender</option>
                        {showMale && <option value="M">Male</option>}
                        {showFemale && <option value="F">Female</option>}
                      </select>
                      {selectedTeam && !hasMale && !hasFemale && (
                        <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '4px 0 0 0' }}>
                          No approved players found for this team
                        </p>
                      )}
                    </>
                  );
                })()}
              </div>

            </div>

            {/* Download Button - bottom right */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1.25rem' }}>
              <button
                onClick={handleDownloadCertificates}
                disabled={downloading || !selectedGender}
                style={{
                  padding: '10px 28px',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: (downloading || !selectedGender) ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '0.95rem',
                  color: 'white',
                  background: 'linear-gradient(135deg, #c00f15, #a00d12)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  opacity: (downloading || !selectedGender) ? 0.6 : 1,
                }}
              >
                {downloading ? (
                  <><i className="fas fa-spinner fa-spin"></i> Generating...</>
                ) : (
                  <><i className="fas fa-download"></i> Download Certificate</>
                )}
              </button>
            </div>
          </div>

          {/* Info card when no selection */}
          {!selectedDownloadEvent && (
            <div style={{ background: 'white', borderRadius: '12px', padding: '3rem', textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
              <i className="fas fa-trophy" style={{ fontSize: '4rem', color: '#d1d5db', marginBottom: '1rem', display: 'block' }}></i>
              <h3 style={{ margin: '0 0 0.5rem 0', color: '#374151' }}>Select a Tournament to Start</h3>
              <p style={{ margin: 0, color: '#6b7280' }}>Choose a published tournament to download certificates for participating teams.</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default CertificateGeneration
